import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Typography, Input } from 'antd';
import ReusableSelect from './ReusableSelect';
import './FirmwareVersionSelection.less';

const { Text } = Typography;

interface FirmwareVersionSelectionProps {
  totalDevices: number; // Total number of selected devices
  fetchFirmwareVersions: any; // API call to fetch firmware versions
  deviceType: string;
  onFirmwareIdChange: (firmwareId: number | undefined) => void;
  selectDevice: Object;
  errorMessage?: string;
  versionList: any  // Receive data from parent
  setErrorMessage?: (error: string) => void;  
}

const FirmwareTypeOption = ['Application']; // TODO: This option will be included 'Wireless'
const FirmwareStatusOption = ['release', 'alpha', 'beta', 'engineering'];

function getFirmwareVersions(data: any[], status: string) {
  const filteredStatus = data?.filter((d: any) => d?.status === status && d?.id !== undefined);
  return filteredStatus?.map((d: any) => ({
    label: d?.swVersion,
    value: d?.id as number, // Use firmware ID
  }));
}

function getFirmwareVersions1(data: any[], status: string) {
  const filteredStatus = data?.filter((d: any) => d?.fwStatus === status);
  return filteredStatus?.map((d: any) => ({
    label: d?.currentFwVersion
  }));
}

const FirmwareVersionSelection: React.FC<FirmwareVersionSelectionProps> = ({
  totalDevices,
  fetchFirmwareVersions,
  deviceType,
  onFirmwareIdChange, // Accept callback
  selectDevice,
  errorMessage,
  versionList,  // Receive data from parent
  setErrorMessage,
}: any) => {
  const [firmwareType, setFirmwareType] = useState<string>('application'); // Current firmware type
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(); // Selected version
  const [forceUpdate, setForceUpdate] = useState<boolean>(false); // Force update checkbox state
  const [firmwareStatus, setFirmwareStatus] = useState<string>(selectDevice?.[0]?.fwStatus || 'alpha'); // Current firmware status
  const [firmwareList, setFirmwareList] = useState<any[]>(fetchFirmwareVersions); // API Data
  const firmwareVersionOptions = getFirmwareVersions(firmwareList, firmwareStatus);
  const firmwareVersionOptions1 = getFirmwareVersions1(versionList, firmwareStatus);
  const [selectedFwId, setSelectedFwId] = useState<number | undefined>();
  const { t } = useTranslation();

  useEffect(() => {
    setFirmwareList(fetchFirmwareVersions); // Update when new data comes in
  }, [fetchFirmwareVersions]);

  // Default selection if only one firmware option is available
  useEffect(() => {
  if (firmwareVersionOptions.length === 1) {
    handleOnChange(firmwareVersionOptions?.[0].value);
  }
  }, [firmwareVersionOptions]);

  const handleOnChange = (value : string) => {
    // Clear previous selection first
    setSelectedFwId(undefined);
    const selectedFirmware = firmwareVersionOptions.find((fw: any) => fw.value === value);
    setSelectedFwId(selectedFirmware?.value); // Set firmware ID
    onFirmwareIdChange(selectedFirmware?.value); // Pass firmware ID to parent
    // Clear error only if a valid firmware is selected
    if (selectedFirmware?.value) {
      setErrorMessage("");
    }
  }

  return (
    <div className="firmware-version-selection gap-10 flex-column">
      <div className="flex-row align-items-center">
        <Text className="flex-2">{t('Total number of devices')}</Text>
        <Input value={totalDevices} disabled className="flex-2 font-bold" />
      </div>
      <div className="flex-row align-item-baseline">
        <Text className="flex-1">{t('Current Max Firmware Version')}</Text>
        <ReusableSelect
          options={firmwareVersionOptions1}
          defaultValue={selectDevice?.[0]?.currentFwVersion}
          onChange={setSelectedVersion}
          value={selectedVersion}
          disabled={firmwareVersionOptions1.length === 0}
          placeholder='select version'
          className="flex-2 select-dropdown-width dropdown-padding"
        />
      </div>
      {/* Below code will be used in the future */}
      {/* <div className="flex-row align-item-baseline">
        <Text className="flex-1">{t('Firmware Type')}</Text>
        <ReusableSelect
          options={FirmwareTypeOption}
          onChange={setFirmwareType}
          value={firmwareType}
          className="flex-2 select-dropdown-width dropdown-padding"
        />
      </div> */}
      <div className="flex-row align-item-baseline">
        <Text className="flex-1">{t('Firmware Status')}</Text>
        <ReusableSelect
          options={FirmwareStatusOption}
          onChange={setFirmwareStatus}
          value={firmwareStatus}
          className="flex-2 select-dropdown-width dropdown-padding"
        />
      </div>
      <div className="flex-row align-item-baseline">
        <Text className="flex-1">{t('Next Version')}</Text>
        <div className='flex-2'>
          <ReusableSelect
            options={firmwareVersionOptions} // Now an array of {label, value}
            onChange={handleOnChange}
            value={selectedFwId} // Value should be the firmware ID
            disabled={firmwareVersionOptions.length === 0}
            placeholder='select version'
            className="flex-2 select-dropdown-width dropdown-padding"
          />
          {errorMessage && 
          <div className="error-text next-version-error-message">
            {errorMessage}
          </div>}
        </div>
      </div>
    </div>
  );
};

export default FirmwareVersionSelection;
